import React, { useMemo } from 'react';
import Icon from '../Icon';
import T from '../components/EditableText';
import { useSiteContent } from '../SiteContent';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import BetreuungsCheck from './BetreuungsCheck';
import AudioBeitraege from './AudioBeitraege';
import TickList from '../components/TickList';
import Testimonials from '../components/Testimonials';
import { useKampagnenTelefon } from './M38Layout';
import { track, M38_EVENTS } from './tracking';
import HeroPicture from '../components/HeroPicture';

/**
 * Die Landingpage der Radio38-Kampagne — eine einzige lange Seite.
 *
 * Reihenfolge nach dem Konzept: Wiedererkennung → Erklärung → Vertrauen →
 * Ablauf → Betreuungs-Check → Radiobeiträge → Ansprechpartner → Fragen →
 * Abschluss. Jeder Abschnitt endet auf denselben Weg, den Betreuungs-Check;
 * es gibt bewusst kein zweites Ziel.
 *
 * Layout und Farben kommen aus `website.css` (.shell, .split, .autogrid,
 * .card, .btn) — hier steht nur die Anordnung.
 */

const SORGEN = [1, 2, 3, 4];
const LEISTUNGEN = [1, 2, 3, 4];
const VERTRAUEN: { nummer: number; icon: string }[] = [
  { nummer: 1, icon: 'hand-heart' },
  { nummer: 2, icon: 'badge-check' },
  { nummer: 3, icon: 'shield-check' },
  { nummer: 4, icon: 'phone-call' },
  { nummer: 5, icon: 'map-pin' },
];
const SCHRITTE = [1, 2, 3];
const VORTEILE = [1, 2, 3];
const FRAGEN = [1, 2, 3, 4, 5, 6];

const Landing: React.FC = () => {
  const { t } = useSiteContent();
  const telefon = useKampagnenTelefon();

  usePageMeta(
    'Miteinander38 — persönliche 24-Stunden-Betreuung in der Region 38',
    'Persönliche 24-Stunden-Betreuung für Senioren in Gifhorn, Braunschweig, Wolfsburg, Peine und Celle. Kostenlose Beratung von der Seniorenbetreuung Antczak.',
    { image: '/website/img/24h-hero.jpg', standaloneTitle: true, preloadImage: '/website/img/24h-hero.webp' },
  );

  // Die häufigen Fragen als strukturierte Daten. Google zeigt sie bei
  // Kampagnenseiten selten als Rich Result an, aber sie kosten nichts und sind
  // genau die Fragen, die Angehörige in die Suche tippen.
  //
  // useMemo ist hier keine Kür: useJsonLd hängt an der Referenz und schriebe
  // das Script sonst bei jedem Rendern neu — und dieses rendert bei jedem Tipp
  // im Betreuungs-Check.
  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FRAGEN.map((nummer) => ({
        '@type': 'Question',
        name: t(`m38.faq-${nummer}.frage`),
        acceptedAnswer: { '@type': 'Answer', text: t(`m38.faq-${nummer}.antwort`) },
      })),
    }),
    [t],
  );
  useJsonLd('m38-faq', faqJsonLd);

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="hero section">
        <div className="shell split split--hero">
          <div>
            <span className="radio-badge">
              <Icon name="radio" size={15} />
              <T k="m38.hero.badge" />
            </span>
            <h1 className="h-hero">
              <T k="m38.hero.titel" />{' '}
              <span className="accent">
                <T k="m38.hero.titel-akzent" />
              </span>
            </h1>
            <p className="lead">
              <T k="m38.hero.text" />
            </p>
            <div className="btn-row">
              <a
                className="btn btn--primary"
                href="#betreuungscheck"
                onClick={() => track(M38_EVENTS.cta, { stelle: 'hero' })}
              >
                <T k="m38.hero.cta" /> <Icon name="arrow-right" size={18} />
              </a>
              <a
                className="btn btn--ghost"
                href={telefon.href}
                onClick={() => track(M38_EVENTS.telefon, { stelle: 'hero' })}
              >
                <Icon name="phone" size={18} /> <T k="m38.hero.cta-telefon" />
              </a>
            </div>
            <p className="hero-micro">
              <span>
                <Icon name="check" size={15} /> <T k="m38.hero.merkmal-1" />
              </span>
              <span>
                <Icon name="check" size={15} /> <T k="m38.hero.merkmal-2" />
              </span>
              <span>
                <Icon name="check" size={15} /> <T k="m38.hero.merkmal-3" />
              </span>
            </p>
          </div>

          <div className="hero-visual">
            <HeroPicture
              className="hero-photo"
              src="/website/img/24h-hero.jpg"
              alt="Seniorin wird von einer Angehörigen im eigenen Zuhause unterstützt"
              width={900}
              height={1000}
            />
            <div className="hero-card">
              <strong>
                <T k="m38.hero.karte.titel" />
              </strong>
              <span className="note">
                <T k="m38.hero.karte.text" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Wiedererkennung ───────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="message-circle" size={15} />
              <T k="m38.sorgen.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.sorgen.titel" />
            </h2>
            <p className="lead">
              <T k="m38.sorgen.text" />
            </p>
          </div>
          <div className="autogrid" style={{ ['--min' as string]: '240px' }}>
            {SORGEN.map((nummer) => (
              <article className="card problem" key={nummer}>
                <div className="num">{String(nummer).padStart(2, '0')}</div>
                <h3 className="h-card">
                  <T k={`m38.sorge-${nummer}.titel`} />
                </h3>
                <p>
                  <T k={`m38.sorge-${nummer}.text`} />
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Was 24h-Betreuung bedeutet ────────────────────────────────────── */}
      <section className="section section--tinted">
        <div className="shell split split--media-left">
          <div className="media">
            <img
              src="/website/img/24h-grundpflege.jpg"
              alt="Betreuungskraft reicht einer Seniorin eine Tasse Kaffee im Wohnzimmer"
              width={760}
              height={620}
              loading="lazy"
            />
          </div>
          <div>
            <span className="eyebrow">
              <Icon name="house" size={15} />
              <T k="m38.erklaerung.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.erklaerung.titel" />
            </h2>
            <p className="lead" style={{ marginTop: 16 }}>
              <T k="m38.erklaerung.text" />
            </p>
            <div style={{ marginTop: 26 }}>
              <TickList
                keys={LEISTUNGEN.map((nummer) => `m38.erklaerung.leistung-${nummer}`)}
              />
            </div>
            <p className="disclaimer">
              <T k="m38.erklaerung.hinweis" />
            </p>
          </div>
        </div>
      </section>

      {/* ── Warum Antczak ─────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="heart-handshake" size={15} />
              <T k="m38.vertrauen.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.vertrauen.titel" />
            </h2>
            <p className="lead">
              <T k="m38.vertrauen.text" />
            </p>
          </div>
          <div className="autogrid" style={{ ['--min' as string]: '210px' }}>
            {VERTRAUEN.map(({ nummer, icon }) => (
              <article className="card" key={nummer}>
                <span className="badge-icon badge-icon--sm" aria-hidden="true" style={{ marginBottom: 16 }}>
                  <Icon name={icon} size={20} />
                </span>
                <h3 className="h-card">
                  <T k={`m38.vertrauen-${nummer}.titel`} />
                </h3>
                <p className="note">
                  <T k={`m38.vertrauen-${nummer}.text`} />
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stimmen ───────────────────────────────────────────────────────── */}
      {/*
        Steht bewusst direkt hinter „Betreuung ist Vertrauenssache": Dort wird
        die Behauptung aufgestellt, hier steht der Beleg dafür — von Familien,
        nicht von uns.
      */}
      <section className="section section--tinted">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="message-circle" size={15} />
              <T k="m38.stimmen.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.stimmen.titel" />
            </h2>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* ── Ablauf ────────────────────────────────────────────────────────── */}
      <section className="section section--dark">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="clipboard-list" size={15} />
              <T k="m38.ablauf.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.ablauf.titel" />
            </h2>
            <p className="lead">
              <T k="m38.ablauf.text" />
            </p>
          </div>
          <div className="autogrid" style={{ ['--min' as string]: '260px' }}>
            {SCHRITTE.map((nummer) => (
              <article className="step" key={nummer}>
                <span className="badge-icon badge-icon--step">{String(nummer).padStart(2, '0')}</span>
                <h3 className="h-card">
                  <T k={`m38.schritt-${nummer}.titel`} />
                </h3>
                <p>
                  <T k={`m38.schritt-${nummer}.text`} />
                </p>
              </article>
            ))}
          </div>
          <div style={{ marginTop: 26 }}>
            <a
              className="btn btn--primary"
              href="#betreuungscheck"
              onClick={() => track(M38_EVENTS.cta, { stelle: 'ablauf' })}
            >
              <T k="m38.ablauf.cta" /> <Icon name="arrow-right" size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* ── Betreuungs-Check ──────────────────────────────────────────────── */}
      <section id="betreuungscheck" className="section" style={{ scrollMarginTop: 90 }}>
        <div className="shell split split--owner">
          <div>
            <span className="eyebrow">
              <Icon name="file-check" size={15} />
              <T k="m38.check.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.check.titel" />
            </h2>
            <p className="lead" style={{ marginTop: 16 }}>
              <T k="m38.check.text" />
            </p>

            <div className="stack" style={{ ['--gap' as string]: '16px', marginTop: 26 }}>
              {VORTEILE.map((nummer) => (
                <div className="benefit" key={nummer}>
                  <Icon name="check" size={18} aria-hidden="true" />
                  <div>
                    <strong>
                      <T k={`m38.check.vorteil-${nummer}.titel`} />
                    </strong>
                    <p>
                      <T k={`m38.check.vorteil-${nummer}.text`} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <BetreuungsCheck />
        </div>
      </section>

      {/* ── Radiobeiträge ─────────────────────────────────────────────────── */}
      <section id="beitraege" className="section section--tinted" style={{ scrollMarginTop: 90 }}>
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="radio" size={15} />
              <T k="m38.radio.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.radio.titel" />
            </h2>
            <p className="lead">
              <T k="m38.radio.text" />
            </p>
          </div>
          <AudioBeitraege />
          <div style={{ marginTop: 26 }}>
            <p className="lead" style={{ marginBottom: 14 }}>
              <T k="m38.radio.anschluss" />
            </p>
            <a
              className="btn btn--primary"
              href="#betreuungscheck"
              onClick={() => track(M38_EVENTS.cta, { stelle: 'radio' })}
            >
              <T k="m38.radio.cta" /> <Icon name="arrow-right" size={18} />
            </a>
          </div>
        </div>
      </section>

      {/*
        ── Ansprechpartner ─────────────────────────────────────────────────
        Namen und Aufgaben kommen über `team.1` / `team.2` aus den Stammdaten,
        damit der Kunde eine Rolle an einer Stelle ändern kann. Die Fotos stehen
        dagegen fest im Code. Wer die Reihenfolge von `TEAM` in `siteData.ts`
        ändert, setzt hier also fremde Namen unter die Bilder — an dieser Stelle
        stehen bewusst Roman Antczak (1) und Ilona Zdrojewska (2).
      */}
      <section className="section">
        <div className="shell split split--owner">
          <div className="people">
            <div className="portrait">
              <img
                src="/website/img/roman-portrait.jpg"
                alt="Roman Antczak, Inhaber der Seniorenbetreuung Antczak"
                width={720}
                height={900}
                loading="lazy"
              />
              <div className="portrait-label">
                <strong>{t('team.1.name')}</strong>
                <span>{t('team.1.rolle')}</span>
              </div>
            </div>
            <div className="portrait">
              <img
                src="/website/img/team-ilona.jpg"
                alt="Ilona Zdrojewska, Leitung 24-Stunden-Betreuung"
                width={520}
                height={620}
                loading="lazy"
              />
              <div className="portrait-label">
                <strong>{t('team.2.name')}</strong>
                <span>{t('team.2.rolle')}</span>
              </div>
            </div>
          </div>
          <div>
            <span className="eyebrow">
              <Icon name="users" size={15} />
              <T k="m38.personen.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.personen.titel" />
            </h2>
            <p className="quote">
              <T k="m38.personen.zitat" />
            </p>
            <p className="lead">
              <T k="m38.personen.text" />
            </p>
            <div style={{ marginTop: 22 }}>
              <a
                className="btn btn--primary"
                href="#betreuungscheck"
                onClick={() => track(M38_EVENTS.cta, { stelle: 'ansprechpartner' })}
              >
                <T k="m38.personen.cta" /> <Icon name="arrow-right" size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Häufige Fragen ────────────────────────────────────────────────── */}
      <section className="section section--tinted">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">
              <Icon name="message-circle" size={15} />
              <T k="m38.faq.eyebrow" />
            </span>
            <h2 className="h-section">
              <T k="m38.faq.titel" />
            </h2>
          </div>
          <div className="faq-list">
            {FRAGEN.map((nummer) => (
              <details key={nummer}>
                <summary>
                  <T k={`m38.faq-${nummer}.frage`} />
                </summary>
                <p>
                  <T k={`m38.faq-${nummer}.antwort`} />
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Abschluss ─────────────────────────────────────────────────────── */}
      <section className="section section--dark">
        <div className="shell final-inner">
          <div>
            <h2 className="h-section">
              <T k="m38.abschluss.titel" />
            </h2>
            <p className="lead" style={{ marginTop: 14 }}>
              <T k="m38.abschluss.text" />
            </p>
          </div>
          <div className="final-actions">
            <a
              className="btn btn--primary"
              href="#betreuungscheck"
              onClick={() => track(M38_EVENTS.cta, { stelle: 'abschluss' })}
            >
              <T k="m38.abschluss.cta" />
            </a>
            <a
              className="btn btn--on-dark"
              href={telefon.href}
              onClick={() => track(M38_EVENTS.telefon, { stelle: 'abschluss' })}
            >
              <Icon name="phone" size={18} /> {telefon.label}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Landing;
